/**
 * Store Filter Component
 * 
 * Reusable filter component for stores list with multiple filter options.
 * Supports filtering by status, province, city, sales rep, and sales group.
 * 
 * @module StoreFilter
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows, Transitions } from '../../styles/ThemeStyles';

export interface StoreFilterOptions {
  status?: 'all' | 'active' | 'inactive' | 'pending' | 'archived';
  province?: string;
  city?: string;
  salesRep?: string;
  salesGroup?: string;
}

interface StoreFilterProps {
  onFilterChange: (filters: StoreFilterOptions) => void;
  availableProvinces?: string[];
  availableCities?: string[];
  availableSalesReps?: Array<{ id: string; name: string }>;
  availableSalesGroups?: string[];
  showStatusFilter?: boolean;
  showSalesRepFilter?: boolean;
  showSalesGroupFilter?: boolean;
  initialFilters?: StoreFilterOptions;
}

const FilterContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const FilterButton = styled.button<{ isOpen: boolean; hasActiveFilters: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  background-color: ${props => props.hasActiveFilters ? ThemeColors.primary.main : '#fff'};
  color: ${props => props.hasActiveFilters ? '#fff' : ThemeColors.text.primary};
  border: 1px solid ${props => props.hasActiveFilters ? ThemeColors.primary.main : ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  cursor: pointer;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};
  position: relative;

  &:hover {
    background-color: ${props => props.hasActiveFilters ? ThemeColors.primary.dark : ThemeColors.gray[50]};
    border-color: ${props => props.hasActiveFilters ? ThemeColors.primary.dark : ThemeColors.border.main};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light}40;
  }

  svg {
    width: 16px;
    height: 16px;
    transition: transform ${Transitions.duration.short} ${Transitions.easing.easeInOut};
  }
`;

const FilterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 ${Spacing.xs};
  background-color: ${ThemeColors.error.main};
  color: white;
  border-radius: ${BorderRadius.full};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.bold};
  margin-left: ${Spacing.xs};
`;

const FilterPanel = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 320px;
  max-width: calc(100vw - ${Spacing.xl * 2});
  background-color: #fff;
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.lg};
  box-shadow: ${Shadows.lg};
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-8px)'};
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};
  max-height: calc(100vh - 200px);
  overflow-y: auto;
`;

const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.md};
  border-bottom: 1px solid ${ThemeColors.border.light};
`;

const FilterTitle = styled.h3`
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin: 0;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${ThemeColors.text.secondary};
  cursor: pointer;
  border-radius: ${BorderRadius.sm};
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};

  &:hover {
    background-color: ${ThemeColors.gray[100]};
    color: ${ThemeColors.text.primary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const FilterBody = styled.div`
  padding: ${Spacing.md};
`;

const FilterSection = styled.div`
  margin-bottom: ${Spacing.lg};

  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterLabel = styled.label`
  display: block;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.xs};
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: ${Spacing.sm};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  background-color: #fff;
  color: ${ThemeColors.text.primary};
  cursor: pointer;
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light}40;
  }

  &:hover {
    border-color: ${ThemeColors.border.main};
  }
`;

const FilterFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.sm};
  padding: ${Spacing.md};
  border-top: 1px solid ${ThemeColors.border.light};
  background-color: ${ThemeColors.gray[50]};
`;

const ClearButton = styled.button`
  flex: 1;
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.light};
  background-color: #fff;
  color: ${ThemeColors.text.secondary};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};

  &:hover {
    background-color: ${ThemeColors.gray[100]};
    color: ${ThemeColors.text.primary};
    border-color: ${ThemeColors.border.main};
  }
`;

const ApplyButton = styled.button`
  flex: 1;
  padding: ${Spacing.sm} ${Spacing.md};
  border: none;
  background-color: ${ThemeColors.primary.main};
  color: white;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};

  &:hover {
    background-color: ${ThemeColors.primary.dark};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ActiveFiltersCount = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  flex-wrap: wrap;
  margin-top: ${Spacing.sm};
`;

const ActiveFilterTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  background-color: ${ThemeColors.primary.light};
  color: ${ThemeColors.primary.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
`;

const RemoveFilterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${ThemeColors.primary.main};
  cursor: pointer;
  border-radius: ${BorderRadius.sm};

  &:hover {
    background-color: ${ThemeColors.primary.main};
    color: white;
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

export const StoreFilter: React.FC<StoreFilterProps> = ({
  onFilterChange,
  availableProvinces = [],
  availableCities = [],
  availableSalesReps = [],
  availableSalesGroups = [],
  showStatusFilter = false,
  showSalesRepFilter = false,
  showSalesGroupFilter = false,
  initialFilters = {}
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState<StoreFilterOptions>(initialFilters);

  // Count active filters (excluding 'all' status)
  const activeFiltersCount = Object.values(filters).filter(
    value => value && value !== 'all'
  ).length;

  const hasActiveFilters = activeFiltersCount > 0;

  // Update filters when initialFilters change
  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isOpen && !target.closest('[data-filter-container]')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleFilterChange = (key: keyof StoreFilterOptions, value: string) => {
    const newFilters = {
      ...filters,
      [key]: value === '' ? undefined : value
    };
    setFilters(newFilters);
  };

  const handleApply = () => {
    onFilterChange(filters);
    setIsOpen(false);
  };

  const handleClear = () => {
    const clearedFilters: StoreFilterOptions = {};
    if (showStatusFilter) {
      clearedFilters.status = 'all';
    }
    setFilters(clearedFilters);
    onFilterChange(clearedFilters);
    setIsOpen(false);
  };

  const handleRemoveFilter = (key: keyof StoreFilterOptions) => {
    const newFilters = {
      ...filters,
      [key]: key === 'status' ? 'all' : undefined
    };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const getFilterLabel = (key: keyof StoreFilterOptions): string => {
    const labels: Record<string, string> = {
      status: t('storeFilter.status', 'Status'),
      province: t('storeFilter.province', 'Province'),
      city: t('storeFilter.city', 'City'),
      salesRep: t('storeFilter.salesRep', 'Sales Rep'),
      salesGroup: t('storeFilter.salesGroup', 'Sales Group')
    };
    return labels[key] || key;
  };

  const getFilterValueLabel = (key: keyof StoreFilterOptions, value: string): string => {
    if (key === 'salesRep') {
      const rep = availableSalesReps.find(r => r.id === value);
      return rep?.name || value;
    }
    return value;
  };

  return (
    <FilterContainer data-filter-container>
      <FilterButton
        isOpen={isOpen}
        hasActiveFilters={hasActiveFilters}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Filter size={16} />
        {t('storeVisit.filter', 'Filter')}
        {hasActiveFilters && (
          <FilterBadge>{activeFiltersCount}</FilterBadge>
        )}
        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </FilterButton>

      <FilterPanel isOpen={isOpen}>
        <FilterHeader>
          <FilterTitle>{t('storeFilter.title', 'Filter Stores')}</FilterTitle>
          <CloseButton onClick={() => setIsOpen(false)}>
            <X size={16} />
          </CloseButton>
        </FilterHeader>

        <FilterBody>
          {showStatusFilter && (
            <FilterSection>
              <FilterLabel>{t('storeFilter.status', 'Status')}</FilterLabel>
              <FilterSelect
                value={filters.status || 'all'}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="all">{t('storeFilter.all', 'All')}</option>
                <option value="active">{t('storeFilter.active', 'Active')}</option>
                <option value="inactive">{t('storeFilter.inactive', 'Inactive')}</option>
                <option value="pending">{t('storeFilter.pending', 'Pending')}</option>
                <option value="archived">{t('storeFilter.archived', 'Archived')}</option>
              </FilterSelect>
            </FilterSection>
          )}

          {availableProvinces.length > 0 && (
            <FilterSection>
              <FilterLabel>{t('storeFilter.province', 'Province')}</FilterLabel>
              <FilterSelect
                value={filters.province || ''}
                onChange={(e) => handleFilterChange('province', e.target.value)}
              >
                <option value="">{t('storeFilter.allProvinces', 'All Provinces')}</option>
                {availableProvinces.map(province => (
                  <option key={province} value={province}>{province}</option>
                ))}
              </FilterSelect>
            </FilterSection>
          )}

          {availableCities.length > 0 && (
            <FilterSection>
              <FilterLabel>{t('storeFilter.city', 'City')}</FilterLabel>
              <FilterSelect
                value={filters.city || ''}
                onChange={(e) => handleFilterChange('city', e.target.value)}
              >
                <option value="">{t('storeFilter.allCities', 'All Cities')}</option>
                {availableCities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </FilterSelect>
            </FilterSection>
          )}

          {showSalesRepFilter && availableSalesReps.length > 0 && (
            <FilterSection>
              <FilterLabel>{t('storeFilter.salesRep', 'Sales Rep')}</FilterLabel>
              <FilterSelect
                value={filters.salesRep || ''}
                onChange={(e) => handleFilterChange('salesRep', e.target.value)}
              >
                <option value="">{t('storeFilter.allSalesReps', 'All Sales Reps')}</option>
                {availableSalesReps.map(rep => (
                  <option key={rep.id} value={rep.id}>{rep.name}</option>
                ))}
              </FilterSelect>
            </FilterSection>
          )}

          {showSalesGroupFilter && availableSalesGroups.length > 0 && (
            <FilterSection>
              <FilterLabel>{t('storeFilter.salesGroup', 'Sales Group')}</FilterLabel>
              <FilterSelect
                value={filters.salesGroup || ''}
                onChange={(e) => handleFilterChange('salesGroup', e.target.value)}
              >
                <option value="">{t('storeFilter.allSalesGroups', 'All Sales Groups')}</option>
                {availableSalesGroups.map(group => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </FilterSelect>
            </FilterSection>
          )}

          {hasActiveFilters && (
            <ActiveFiltersCount>
              {Object.entries(filters).map(([key, value]) => {
                if (!value || value === 'all') return null;
                return (
                  <ActiveFilterTag key={key}>
                    {getFilterLabel(key as keyof StoreFilterOptions)}: {getFilterValueLabel(key as keyof StoreFilterOptions, value)}
                    <RemoveFilterButton
                      onClick={() => handleRemoveFilter(key as keyof StoreFilterOptions)}
                    >
                      <X size={12} />
                    </RemoveFilterButton>
                  </ActiveFilterTag>
                );
              })}
            </ActiveFiltersCount>
          )}
        </FilterBody>

        <FilterFooter>
          <ClearButton onClick={handleClear}>
            {t('storeFilter.clear', 'Clear')}
          </ClearButton>
          <ApplyButton onClick={handleApply}>
            {t('storeFilter.apply', 'Apply')}
          </ApplyButton>
        </FilterFooter>
      </FilterPanel>
    </FilterContainer>
  );
};

